import { useQuery } from '@tanstack/react-query';
import {
  BarChart3, CheckCircle, Crosshair, Eye, Layers, Link2,
  RefreshCcw, Shield, TrendingUp, Zap, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardHeader, CardTitle, PageHeader, ProductIntro, SectionEmptyState } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

interface MyProduct {
  productCode: string;
  displayName: string;
  shortDescription: string;
  category: string;
  productGloballyEnabled: boolean;
  clientEnabled: boolean;
  effectiveDeductionAmount: number;
  billingUnit: string;
}

const PRODUCT_CONFIG: Record<string, { icon: React.ReactNode; gradient: string; iconBg: string }> = {
  VERIFY:      { icon: <CheckCircle className="h-6 w-6" />,  gradient: 'from-blue-600 to-primary-500',    iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400'          },
  VERIFY_PLUS: { icon: <Zap className="h-6 w-6" />,          gradient: 'from-indigo-600 to-violet-500',   iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400'   },
  SIGNAL:      { icon: <Crosshair className="h-6 w-6" />,    gradient: 'from-violet-600 to-purple-500',   iconBg: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400'   },
  SHIELD:      { icon: <Shield className="h-6 w-6" />,       gradient: 'from-emerald-600 to-teal-500',    iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'},
  DETECT:      { icon: <Eye className="h-6 w-6" />,          gradient: 'from-orange-600 to-amber-500',    iconBg: 'bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400'   },
  TRUST:       { icon: <BarChart3 className="h-6 w-6" />,    gradient: 'from-cyan-600 to-sky-500',        iconBg: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-400'            },
  STUCK:       { icon: <TrendingUp className="h-6 w-6" />,   gradient: 'from-teal-600 to-emerald-500',    iconBg: 'bg-teal-50 text-teal-600 dark:bg-teal-500/15 dark:text-teal-400'            },
  STUCK_PLUS:  { icon: <RefreshCcw className="h-6 w-6" />,   gradient: 'from-amber-600 to-orange-500',    iconBg: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'        },
  CONNECT:     { icon: <Link2 className="h-6 w-6" />,        gradient: 'from-blue-600 to-indigo-500',     iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400'            },
};

const CATEGORY_COLORS: Record<string, string> = {
  VERIFICATION:           'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  FRAUD_PROTECTION:       'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  RISK_INTELLIGENCE:      'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  RISK_SCORING:           'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  RECOVERY_INTELLIGENCE:  'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  CRM_INTELLIGENCE:       'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  TARGETING_INTELLIGENCE: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300',
  JOURNEY_INTELLIGENCE:   'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  DEVICE_INTELLIGENCE:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  NETWORK_INTELLIGENCE:   'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
};

const CATEGORY_LABELS: Record<string, string> = {
  VERIFICATION:           'Verification',
  FRAUD_PROTECTION:       'Fraud Protection',
  RISK_INTELLIGENCE:      'Risk Intelligence',
  RISK_SCORING:           'Risk Scoring',
  RECOVERY_INTELLIGENCE:  'Recovery Intelligence',
  CRM_INTELLIGENCE:       'CRM Intelligence',
  TARGETING_INTELLIGENCE: 'Targeting Intelligence',
  JOURNEY_INTELLIGENCE:   'Journey Intelligence',
  DEVICE_INTELLIGENCE:    'Device Intelligence',
  NETWORK_INTELLIGENCE:   'Network Intelligence',
};

const PRODUCT_DOCS: Record<string, string> = {
  VERIFY:      '/portal/docs#verify-n',
  VERIFY_PLUS: '/portal/docs#verify-n-plus',
  SIGNAL:      '/portal/docs#target',
  SHIELD:      '/portal/docs#shield',
  DETECT:      '/portal/docs#detect',
  TRUST:       '/portal/docs#trust',
  STUCK:       '/portal/docs#stuck',
  STUCK_PLUS:  '/portal/docs#stuck-plus',
  CONNECT:     '/portal/docs#connect',
};

async function fetchMyProducts(): Promise<MyProduct[]> {
  const res = await api.get<ResponseEnvelope<MyProduct[]>>(Endpoints.me.products);
  return unwrap(res);
}

export function ClientProductsPage() {
  const { data: products, isLoading, isError } = useQuery({
    queryKey: ['me', 'products'],
    queryFn: fetchMyProducts,
    staleTime: 5 * 60_000,
  });

  const enabled = products?.filter((p) => p.clientEnabled) ?? [];
  const disabled = products?.filter((p) => !p.clientEnabled) ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="My Products"
        subtitle="Active Authmatech products available on your account."
        badge={
          products && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/25">
              {enabled.length} active
            </span>
          )
        }
      />

      <ProductIntro
        icon={Layers}
        tone="neutral"
        eyebrow="The Authmatech platform"
        title="Everything Authmatech does for your account, built in layers on silent verification."
        points={['Authentication', 'Customer Intelligence', 'Customer Journey Recovery']}
      >
        <p>
          <strong>Authentication</strong> (Verify &amp; Verify+) confirms a customer controls their number with no
          OTP. <strong>Customer Intelligence</strong> (Trust, Shield, Detect) scores identities, decides on live
          sessions, and catches coordinated abuse. <strong>Customer Journey Recovery</strong> (Stuck+, Connect,
          Target) wins back stuck customers, syncs intelligence into your CRM, and reconnects high-intent visitors.
        </p>
        <p>
          The products below are active on your account — request any you don’t have yet and we’ll switch them on.
        </p>
      </ProductIntro>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0,1,2,3].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-border bg-surface p-5 shadow-card dark:shadow-card-dark">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="h-10 w-10 rounded-xl bg-surface-muted" />
                <div className="h-5 w-14 rounded-full bg-surface-muted" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-24 rounded bg-surface-muted" />
                <div className="h-3 w-full rounded bg-surface-muted" />
                <div className="h-3 w-3/4 rounded bg-surface-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <Card>
          <p className="py-8 text-center text-sm text-rose-500">Failed to load products. Please try refreshing.</p>
        </Card>
      ) : !products?.length ? (
        <Card>
          <SectionEmptyState
            icon={Layers}
            title="No products enabled yet"
            description="Contact Authmatech to enable products on your account."
            className="py-16"
          />
        </Card>
      ) : (
        <>
          {enabled.length > 0 && (
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-fg-subtle">Active</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {enabled.map((p) => <ProductCard key={p.productCode} product={p} />)}
              </div>
            </div>
          )}
          {disabled.length > 0 && (
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-fg-subtle">Inactive</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {disabled.map((p) => <ProductCard key={p.productCode} product={p} />)}
              </div>
            </div>
          )}
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Need more products?</CardTitle>
        </CardHeader>
        <p className="text-sm text-fg-muted">
          Contact your Authmatech account manager to enable additional products or adjust your pricing
          configuration. Each product deducts from your credit balance per API call.
        </p>
        <Link to="/portal/docs" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
          View API documentation <ArrowRight className="h-3 w-3" />
        </Link>
      </Card>
    </div>
  );
}

function ProductCard({ product: p }: { product: MyProduct }) {
  const cfg = PRODUCT_CONFIG[p.productCode] ?? {
    icon: <Layers className="h-6 w-6" />,
    gradient: 'from-gray-600 to-gray-500',
    iconBg: 'bg-surface-muted text-fg-muted',
  };

  return (
    <div className={cn(
      'group relative overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-card transition-all duration-200 hover:shadow-card-md dark:shadow-card-dark flex flex-col gap-3',
      !p.clientEnabled && 'opacity-60',
    )}>
      <div className={cn('absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r', cfg.gradient)} />

      <div className="flex items-start justify-between gap-2">
        <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl shadow-inner-sm', cfg.iconBg)}>
          {cfg.icon}
        </div>
        <Badge tone={p.clientEnabled ? 'success' : 'neutral'} dot>
          {p.clientEnabled ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <div>
        <p className="text-sm font-bold text-fg">{p.displayName}</p>
        <span className={cn(
          'mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium',
          CATEGORY_COLORS[p.category] ?? 'bg-surface-muted text-fg-muted',
        )}>
          {CATEGORY_LABELS[p.category] ?? p.category}
        </span>
        <p className="mt-2 text-xs text-fg-muted leading-relaxed">{p.shortDescription}</p>
      </div>

      <div className="mt-auto flex items-center justify-between pt-3 border-t border-border">
        <div className="text-xs text-fg-muted">
          <span className="font-bold text-fg">{p.effectiveDeductionAmount} credit</span>
          {' '}/ {p.billingUnit}
        </div>
        <Link
          to={PRODUCT_DOCS[p.productCode] ?? '/portal/docs'}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
        >
          View docs <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
